import { Heading, Paragraph } from '@zillow/constellation-10';
import { ApplicationConfigurationCustomQuestion } from '@zg-rentals/ts-capps-client';
import { keyBy } from 'lodash-es';
import type { Document } from '../../../../../models/rental-application';
import { useRetrieveActiveDocuments } from '../../../../../hooks/useRetrieveActiveDocuments';
import {
  getCustomQuestionDocuments,
  getHasPets,
  getPetSummary,
  getIsCustomQuestionEnabled,
} from '../../../../../utils/customQuestions';
import { pluralize } from '../../../../../utils/formatters';
import type { SupplementalSectionProps } from '../../../../ApplicationEdit/Sections/7Supplemental/supplementalSections';
import { OverviewDocumentThumbnails } from '../3IncomeHistory/OverviewDocumentThumbnails';

function DocumentsOverview({
  heading,
  documents,
  details,
  isPending,
}: {
  heading: string;
  documents: Array<Document>;
  details: Array<Document> | undefined;
  isPending: boolean;
}) {
  const detailsById = keyBy(details, 'id');
  const withDetails = documents.map((document) => detailsById[document.id ?? ''] ?? document);

  return (
    <>
      <Heading level={6} css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
        {heading}
      </Heading>
      <Paragraph>{documents.length > 0 ? pluralize('document', documents.length) : 'No documents added'}</Paragraph>
      {documents.length > 0 && <OverviewDocumentThumbnails documents={withDetails} isPending={isPending} />}
    </>
  );
}

export function PetRecordsOverview({ answers }: SupplementalSectionProps) {
  const isEnabled = (question: ApplicationConfigurationCustomQuestion) => getIsCustomQuestionEnabled(answers, question);
  const hasPets = getHasPets(answers);
  const veterinaryRecords = getCustomQuestionDocuments(
    answers,
    ApplicationConfigurationCustomQuestion.VETERINARY_RECORDS_UPLOAD,
  );
  const petPhotos = getCustomQuestionDocuments(answers, ApplicationConfigurationCustomQuestion.PET_PHOTO_UPLOAD);
  const certifications = getCustomQuestionDocuments(
    answers,
    ApplicationConfigurationCustomQuestion.ESA_CERTIFICATION_UPLOAD,
  );
  const { documents: details, isPending } = useRetrieveActiveDocuments([
    ...veterinaryRecords,
    ...petPhotos,
    ...certifications,
  ]);

  return (
    <>
      {hasPets && (
        <>
          <Heading level={6} css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
            Pet information
          </Heading>
          <Paragraph>{getPetSummary(answers)}</Paragraph>
        </>
      )}
      {hasPets && isEnabled(ApplicationConfigurationCustomQuestion.VETERINARY_RECORDS_UPLOAD) && (
        <DocumentsOverview
          heading="Veterinary records"
          documents={veterinaryRecords}
          details={details}
          isPending={isPending}
        />
      )}
      {hasPets && isEnabled(ApplicationConfigurationCustomQuestion.PET_PHOTO_UPLOAD) && (
        <DocumentsOverview heading="Pet photos" documents={petPhotos} details={details} isPending={isPending} />
      )}
      {isEnabled(ApplicationConfigurationCustomQuestion.ESA_CERTIFICATION_UPLOAD) && (
        <>
          <Heading level={6} css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
            Service animal or emotional support animal (ESA)?
          </Heading>
          <Paragraph>
            {answers.hasServiceAnimal == null ? 'Not answered' : answers.hasServiceAnimal ? 'Yes' : 'No'}
          </Paragraph>
          {answers.hasServiceAnimal === true && (
            <DocumentsOverview
              heading="Service animal or ESA certification"
              documents={certifications}
              details={details}
              isPending={isPending}
            />
          )}
        </>
      )}
    </>
  );
}
